import { prefersReducedMotion } from '@theme/utilities';

/**
 * Attribute added to elements that opt into scroll-triggered reveal.
 * @constant {string}
 */
const REVEAL_ATTRIBUTE = 'data-scroll-reveal';

/**
 * Attribute set once an element has entered the viewport and its reveal transition should play.
 * @constant {string}
 */
const REVEALED_ATTRIBUTE = 'data-scroll-revealed';

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * Lazily creates the shared IntersectionObserver used to trigger reveals.
 * @returns {IntersectionObserver}
 */
function getObserver() {
  if (!observer) {
    observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.15,
      rootMargin: '0px 0px -10% 0px',
    });
  }

  return observer;
}

/**
 * @param {IntersectionObserverEntry[]} entries
 */
function handleIntersect(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    entry.target.setAttribute(REVEALED_ATTRIBUTE, '');
    getObserver().unobserve(entry.target);
  }
}

/**
 * Observes every not-yet-observed `[data-scroll-reveal]` element on the page.
 * Under `prefers-reduced-motion`, elements are revealed immediately instead of observed.
 */
function observeRevealTargets() {
  const targets = document.querySelectorAll(`[${REVEAL_ATTRIBUTE}]:not([${REVEALED_ATTRIBUTE}])`);

  for (const target of targets) {
    if (prefersReducedMotion()) {
      target.setAttribute(REVEALED_ATTRIBUTE, '');
      continue;
    }

    getObserver().observe(target);
  }
}

observeRevealTargets();

// Re-scan when the theme editor swaps a section's markup so newly rendered reveal targets are picked up.
document.addEventListener('shopify:section:load', observeRevealTargets);
